import {useEffect} from 'react';
import {useForm, type UseFormProps} from 'react-hook-form';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {i18n} from '@netx/core-next/locales/i18n-instance';
import {AssetViewFormSchema, type IAssetViewFormValues} from '../schema';

/** Default values for the form */
const defaultValues: IAssetViewFormValues = {
	description: '',
	name: '',
};

export interface UseAssetViewFormParams extends UseFormProps<IAssetViewFormValues> {
	initialValues?: Partial<IAssetViewFormValues>;
	submit: (data: IAssetViewFormValues) => Promise<void>;
}

/**
 * react-hook-form for editing a system group
 *
 * @example
 * ```tsx
 * const {form, onSubmit, reset} = useAssetViewForm({
 * 	initialValues: {
 * 		description: 'Lorem ipsum dolar sit amet',
 *      name: 'Foo',
 * 	},
 * 	submit: async (data) => {
 * 		// submit the form
 * 	},
 * });
 *
 * return (
 * 	<form onSubmit={onSubmit}>...</form>
 * );
 * ```
 */
export const useAssetViewForm = ({
	initialValues = defaultValues,
	submit,
	...props
}: UseAssetViewFormParams) => {
	const form = useForm<IAssetViewFormValues>({
		mode: 'all',
		criteriaMode: 'all',
		defaultValues: initialValues,
		...props,
	});

	/** Reset the form to the default values */
	const reset = useConstCallback((values?: Partial<IAssetViewFormValues>) =>
		form.reset({...defaultValues, ...values}),
	);

	useEffect(() => {
		reset(initialValues);
	}, [initialValues]);

	/** Form submission handler */
	const onSubmit = form.handleSubmit(async (data) => {
		return submit(data).catch((error) => {
			// const _error = getAssetViewError('update', error.code);
			form.setError('root', {
				message: i18n.t('i18n.errorRequestFailed'), // typeof _error === 'function' ? _error(error.message) : _error,
				type: 'error',
			});
		});
	});

	return {
		form,
		onSubmit,
		reset,
	};
};
